import type { LearnerSearchResults } from '~/helpers/learner/learner-search'
import { useTranslation } from 'react-i18next'
import { isPath, steps } from '~/helpers/learner/session-view'
import HighlightedText from '~/pages/scorm/components/learner-search/HighlightedText'
import { optionId } from '~/pages/scorm/components/learner-search/option-id'
import SearchResultItem from '~/pages/scorm/components/learner-search/SearchResultItem'

type IProps = {
  listboxId: string
  results: LearnerSearchResults
  query: string
  activeIndex: number
  onSelect: (target: string) => void
  onHover: (index: number) => void
}

/**
 * Le panneau de résultats (#109) : formations puis modules, dans une seule
 * listbox — les flèches les parcourent d'un trait, l'indice d'une option suit
 * donc cet ordre.
 */
function SearchResults({ listboxId, results, query, activeIndex, onSelect, onHover }: IProps) {
  const { t } = useTranslation()
  const offset = results.sessions.length

  if (offset + results.modules.length === 0) {
    // Lu par la région d'annonce de `SearchCombobox`, pas ici : pas de `role`.
    return <p className="px-3 py-4 text-sm text-muted-foreground">{t('common.noResultsFor', { query })}</p>
  }

  const group = (key: 'sessions' | 'modules', children: React.ReactNode) => (
    <div role="group" aria-labelledby={`${listboxId}-${key}`} className="flex flex-col gap-0.5">
      <p id={`${listboxId}-${key}`} role="presentation" className="px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground">
        {t(`learnerSearch.group.${key}`)}
      </p>
      {children}
    </div>
  )

  return (
    <div id={listboxId} role="listbox" aria-label={t('learnerSearch.label')} className="flex flex-col gap-2">
      {results.sessions.length > 0 && group('sessions', results.sessions.map((hit, index) => (
        <SearchResultItem
          key={hit.session.id}
          id={optionId(listboxId, index)}
          active={index === activeIndex}
          title={hit.session.name}
          query={query}
          context={isPath(hit.session)
            ? t('home.type.path', { count: steps(hit.session).length })
            : (
                <>
                  {t('home.type.course')}
                  {hit.moduleTitle && (
                    <>
                      {' '}
                      ·
                      <HighlightedText text={hit.moduleTitle} query={query} />
                    </>
                  )}
                </>
              )}
          status={hit.status}
          detail={hit.locked
            ? t('learnerSearch.locked')
            : hit.status === 'INCOMPLETE' ? t('learnerSearch.progress', { pct: hit.progress }) : undefined}
          onSelect={() => onSelect(hit.target)}
          onHover={() => onHover(index)}
        />
      )))}
      {results.modules.length > 0 && group('modules', results.modules.map((hit, index) => (
        <SearchResultItem
          key={hit.step.id}
          id={optionId(listboxId, offset + index)}
          active={offset + index === activeIndex}
          title={hit.step.module?.title ?? ''}
          query={query}
          context={hit.session.name}
          status={hit.status}
          detail={hit.locked ? t('learnerSearch.locked') : undefined}
          onSelect={() => onSelect(hit.target)}
          onHover={() => onHover(offset + index)}
        />
      )))}
    </div>
  )
}

export default SearchResults
